import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { bowser } from '@yola/ws-sdk';
import highlighter from 'src/js/modules/highlighter';
import getPositioningBoundingArea from '../helpers/get-positioning-bounding-area';

const {
  helpers: { highlighterOffset },
} = highlighter;

const getPositioningArea = (positioningAreaBounds, scrollPosition) => ({
  width: positioningAreaBounds.width,
  height: positioningAreaBounds.height,
  left: positioningAreaBounds.left,
  right: positioningAreaBounds.right,
  top: positioningAreaBounds.top,
  bottom: positioningAreaBounds.bottom,
  bottomWithScrollPosition: positioningAreaBounds.bottom + scrollPosition,
  topWithScrollPosition: positioningAreaBounds.top + scrollPosition,
});

const usePositioningArea = (element, scrollPosition) => {
  const { boundingRect: positioningAreaBounds } = useMemo(
    () => getPositioningBoundingArea(element),
    // we need to recalculate bounding rects on scroll
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [element, scrollPosition]
  );
  const correctionY = bowser.ios ? 0 : scrollPosition;

  const [positioningArea, setPositioningArea] = useState(
    getPositioningArea(positioningAreaBounds, scrollPosition)
  );
  const [clipPath, setClipPath] = useState('');

  const updatePositioningArea = useCallback(
    () => () => setPositioningArea(getPositioningArea(positioningAreaBounds, correctionY)),
    [positioningAreaBounds, correctionY]
  );

  const updateClipPath = useCallback(() => {
    const indent = highlighterOffset.get();
    const elementBounds = element.getBoundingClientRect();
    const xTopLeft = elementBounds.left - positioningArea.left;
    const yTopLeft = elementBounds.top - positioningArea.top;
    const xTopRight = xTopLeft + elementBounds.width + indent * 2;
    const yTopRight = yTopLeft;
    const xBottomLeft = xTopLeft;
    const yBottomLeft = yTopLeft + elementBounds.height + indent * 2;
    const xBottomRight = xTopRight;
    const yBottomRight = yBottomLeft;

    const highlighterBorderWidth = '2px';
    const clamp = (value) =>
      `clamp(${highlighterBorderWidth}, ${value}px, calc(100% - ${highlighterBorderWidth}))`;

    setClipPath(
      `polygon(
        0 0,
        0 100%,
        ${clamp(xTopLeft)} 100%,
        ${clamp(xTopLeft)} ${clamp(yTopLeft)},
        ${clamp(xTopRight)} ${clamp(yTopRight)},
        ${clamp(xBottomRight)} ${clamp(yBottomRight)},
        ${clamp(xBottomLeft)} ${clamp(yBottomLeft)},
        ${clamp(xBottomLeft)} 100%,
        100% 100%,
        100% 0
      )`
    );
  }, [element, positioningArea]);

  useLayoutEffect(() => {
    updatePositioningArea();
    updateClipPath();
  }, [updateClipPath, updatePositioningArea]);

  return {
    positioningAreaBounds: positioningArea,
    positioningAreaClipPath: clipPath,
    updateClipPath,
  };
};

export default usePositioningArea;
